package com.binayshaw7777.myportfolio.components.sections

import androidx.compose.runtime.*
import com.binayshaw7777.myportfolio.OpenSourceDivStyle
import com.binayshaw7777.myportfolio.RevealSpanOnVisible
import com.binayshaw7777.myportfolio.SectionTitleFontStyle
import com.binayshaw7777.myportfolio.components.widgets.changeCursorMode
import com.binayshaw7777.myportfolio.revealText
import com.binayshaw7777.myportfolio.utils.Constants
import com.binayshaw7777.myportfolio.utils.Res
import com.binayshaw7777.myportfolio.widgets.AppearanceAwareImage
import com.binayshaw7777.myportfolio.widgets.StrokeChips
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontOpticalSizing
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

data class TimelinePoint(
    val period: String,
    val count: Int
)

data class ContributionStats(
    val totalPRs: Int = 0,
    val mergedPRs: Int = 0,
    val openPRs: Int = 0,
    val closedPRs: Int = 0,
    val uniqueRepositories: Int = 0,
    val totalAdditions: Int = 0,
    val totalDeletions: Int = 0
)

data class GitHubContributionsSummary(
    val username: String = "binayshaw7777",
    val githubSearchUrl: String = "",
    val stats: ContributionStats = ContributionStats(),
    val primaryTechs: List<String> = emptyList(),
    val activityTimeline: List<TimelinePoint> = emptyList()
)

val ChartBarStyle = CssStyle {
    base {
        Modifier
            .transition(Transition.of("transform", 200.ms), Transition.of("opacity", 200.ms))
    }
    hover {
        Modifier
            .scale(1.1)
    }
}

fun parseGitHubSummary(jsonText: String): GitHubContributionsSummary {
    val dyn = JSON.parse<dynamic>(jsonText)
    val statsDyn = dyn.stats
    val stats = ContributionStats(
        totalPRs = (statsDyn?.totalPRs as? Number)?.toInt() ?: 0,
        mergedPRs = (statsDyn?.mergedPRs as? Number)?.toInt() ?: 0,
        openPRs = (statsDyn?.openPRs as? Number)?.toInt() ?: 0,
        closedPRs = (statsDyn?.closedPRs as? Number)?.toInt() ?: 0,
        uniqueRepositories = (statsDyn?.uniqueRepositories as? Number)?.toInt() ?: 0,
        totalAdditions = (statsDyn?.totalAdditions as? Number)?.toInt() ?: 0,
        totalDeletions = (statsDyn?.totalDeletions as? Number)?.toInt() ?: 0
    )

    val techs = mutableListOf<String>()
    val dynTechs = dyn.primaryTechs
    if (dynTechs != null) {
        val len = (dynTechs.length as? Number)?.toInt() ?: 0
        for (i in 0 until len) {
            val t = dynTechs[i]?.toString()
            if (!t.isNullOrBlank()) techs.add(t)
        }
    }

    val timeline = mutableListOf<TimelinePoint>()
    val dynTimeline = dyn.activityTimeline
    if (dynTimeline != null) {
        val len = (dynTimeline.length as? Number)?.toInt() ?: 0
        for (i in 0 until len) {
            val item = dynTimeline[i]
            val period = item?.period?.toString() ?: ""
            val count = (item?.count as? Number)?.toInt() ?: 0
            if (period.isNotBlank()) {
                timeline.add(TimelinePoint(period, count))
            }
        }
    }

    return GitHubContributionsSummary(
        username = dyn.username?.toString() ?: "binayshaw7777",
        githubSearchUrl = dyn.githubSearchUrl?.toString() ?: "https://github.com/search?q=author%3Abinayshaw7777+is%3Apr+is%3Amerged+-user%3Abinayshaw7777&type=pullrequests",
        stats = stats,
        primaryTechs = if (techs.isNotEmpty()) techs else listOf("Kotlin", "Android SDK", "Jetpack Compose", "Java", "Compose Multiplatform", "Kobweb"),
        activityTimeline = timeline
    )
}

private fun formatNumber(num: Int): String {
    return when {
        num >= 1_000_000 -> "${(num / 1_000_000.0).toString().take(4)}M"
        num >= 1_000 -> "${(num / 1_000.0).toString().take(4)}k"
        else -> num.toString()
    }
}

@Composable
fun Contributions() {
    val breakpoint = rememberBreakpoint()
    var summaryData by remember { mutableStateOf<GitHubContributionsSummary?>(null) }
    var hoveredPoint by remember { mutableStateOf<TimelinePoint?>(null) }

    val colorMode = ColorMode.current
    val isLight = colorMode.isLight

    val subTextColor = if (isLight) Color.rgba(0, 0, 0, 0.65f) else Color.rgba(255, 255, 255, 0.65f)
    val cardBg = if (isLight) Color.rgba(0, 0, 0, 0.02f) else Color.rgba(255, 255, 255, 0.03f)
    val cardBorder = if (isLight) Color.rgba(0, 0, 0, 0.1f) else Color.rgba(255, 255, 255, 0.12f)
    val gridLineColor = if (isLight) Color.rgba(0, 0, 0, 0.06f) else Color.rgba(255, 255, 255, 0.08f)
    val barColor = if (isLight) Colors.Black else Colors.White

    LaunchedEffect(Unit) {
        try {
            val res = window.fetch("/data/github-contributions.json").await()
            if (res.ok) {
                val text = res.text().await()
                summaryData = parseGitHubSummary(text)
            }
        } catch (_: Throwable) {
            // Fallback
        }
    }

    val data = summaryData ?: GitHubContributionsSummary(
        stats = ContributionStats(mergedPRs = 86, uniqueRepositories = 21, totalAdditions = 37734),
        primaryTechs = listOf("Kotlin", "Android SDK", "Jetpack Compose", "Java", "Compose Multiplatform", "Kobweb"),
        activityTimeline = listOf(
            TimelinePoint("2022-02", 29),
            TimelinePoint("2022-03", 34),
            TimelinePoint("2022-04", 3),
            TimelinePoint("2022-05", 1),
            TimelinePoint("2022-08", 2),
            TimelinePoint("2022-09", 1),
            TimelinePoint("2022-10", 5),
            TimelinePoint("2023-01", 1),
            TimelinePoint("2023-02", 1),
            TimelinePoint("2023-09", 3),
            TimelinePoint("2023-10", 1),
            TimelinePoint("2024-04", 1),
            TimelinePoint("2025-04", 1),
            TimelinePoint("2026-07", 2),
            TimelinePoint("2026-08", 1)
        )
    )
    val stats = data.stats
    val timeline = data.activityTimeline
    val maxCount = timeline.maxOfOrNull { it.count }?.coerceAtLeast(1) ?: 1

    Column(
        modifier = Modifier
            .fillMaxSize()
            .fontFamily(Constants.SYNE)
            .margin(top = 100.px)
            .margin(bottom = 25.vh)
            .id("open-source")
    ) {
        RevealSpanOnVisible(threshold = 0.95) { visible, ref ->
            SpanText(
                text = "Open Source",
                modifier = SectionTitleFontStyle.toModifier()
                    .then(if (visible) Modifier.revealText(durationMs = 900) else Modifier),
                ref = ref
            )
        }

        HorizontalDivider()

        // Main Open Source row formatted exactly like KotStep / LeafleKT / JusTap / ReadBud in Project.kt
        Row(
            modifier = OpenSourceDivStyle.toModifier().fillMaxWidth()
                .margin(topBottom = 75.px)
                .changeCursorMode("View on GitHub")
                .onClick {
                    val targetUrl = data.githubSearchUrl.ifBlank {
                        "https://github.com/search?q=author%3Abinayshaw7777+is%3Apr+is%3Amerged+-user%3Abinayshaw7777&type=pullrequests"
                    }
                    window.open(targetUrl, "_blank")
                },
            horizontalArrangement = Arrangement.SpaceAround,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(
                modifier = Modifier
                    .maxWidth(if (breakpoint >= Breakpoint.MD) 75.percent else 100.percent)
                    .margin(topBottom = 20.px, leftRight = 15.px),
                verticalArrangement = Arrangement.SpaceAround
            ) {
                SpanText(
                    text = "${stats.mergedPRs} Merged PRs",
                    modifier = Modifier
                        .fontSize(if (breakpoint >= Breakpoint.MD) 60.px else 36.px)
                        .fontWeight(FontWeight.Bold)
                )

                SpanText(
                    text = "Actively contributing to open-source libraries, Kotlin Multiplatform SDKs, and Android developer tooling across ${stats.uniqueRepositories} public repositories with +${formatNumber(stats.totalAdditions)} lines of code.",
                    modifier = Modifier
                        .fontSize(18.px)
                        .margin(topBottom = 10.px)
                        .fontWeight(401)
                        .fontOpticalSizing(FontOpticalSizing.Auto)
                )

                Row(
                    modifier = Modifier
                        .margin(top = 2.cssRem)
                        .display(DisplayStyle.Flex)
                        .flexWrap(FlexWrap.Wrap),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    data.primaryTechs.forEach { tech ->
                        StrokeChips(
                            modifier = Modifier.margin(right = 0.8.cssRem, top = 0.4.cssRem, bottom = 0.4.cssRem),
                            text = tech
                        )
                    }
                }
            }

            if (breakpoint >= Breakpoint.MD) {
                Spacer()
                Div(attrs = Modifier.id("opensource-arrow").toAttrs()) {
                    AppearanceAwareImage(
                        src = Res.CURVED_HEAD_ARROW,
                        modifier = Modifier.size(40.px).margin(all = 20.px)
                    )
                }
            }
        }

        HorizontalDivider()

        /*
        // XY Activity Timeline Graph (Preserved - commented out per portfolio design preferences)
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .margin(topBottom = 20.px)
                .padding(top = 24.px, bottom = 18.px, leftRight = 24.px)
                .borderRadius(14.px)
                .border(width = 1.px, style = LineStyle.Solid, color = cardBorder)
                .backgroundColor(cardBg)
        ) {
            Column(modifier = Modifier.fillMaxWidth()) {
                // Graph Header
                Row(
                    modifier = Modifier.fillMaxWidth().margin(bottom = 24.px),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.px)
                    ) {
                        SpanText(
                            text = "CONTRIBUTION TIMELINE (XY)",
                            modifier = Modifier
                                .fontFamily(Constants.INTER)
                                .fontSize(12.px)
                                .fontWeight(FontWeight.Bold)
                                .letterSpacing(1.px)
                                .color(subTextColor)
                        )
                    }

                    // Hover indicator info
                    val activeLabel = hoveredPoint?.let { "${it.period} : ${it.count} PRs merged" } ?: "Hover bars for details"
                    SpanText(
                        text = activeLabel,
                        modifier = Modifier
                            .fontFamily(Constants.INTER)
                            .fontSize(12.px)
                            .fontWeight(FontWeight.Medium)
                            .color(if (hoveredPoint != null) (if (isLight) Colors.Black else Colors.White) else subTextColor)
                    )
                }

                // XY Chart Area
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(160.px),
                    verticalAlignment = Alignment.Bottom
                ) {
                    // Y-Axis Ticks
                    Column(
                        modifier = Modifier
                            .height(100.percent)
                            .margin(right = 16.px),
                        verticalArrangement = Arrangement.SpaceBetween,
                        horizontalAlignment = Alignment.End
                    ) {
                        SpanText(
                            text = "$maxCount",
                            modifier = Modifier.fontFamily(Constants.INTER).fontSize(11.px).color(subTextColor)
                        )
                        SpanText(
                            text = "${maxCount / 2}",
                            modifier = Modifier.fontFamily(Constants.INTER).fontSize(11.px).color(subTextColor)
                        )
                        SpanText(
                            text = "0",
                            modifier = Modifier.fontFamily(Constants.INTER).fontSize(11.px).color(subTextColor)
                        )
                    }

                    // Bars Container
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .fillMaxHeight()
                            .styleModifier {
                                property("border-left", "1px solid $gridLineColor")
                                property("border-bottom", "1px solid $gridLineColor")
                            }
                            .padding(leftRight = 8.px),
                        horizontalArrangement = Arrangement.SpaceAround,
                        verticalAlignment = Alignment.Bottom
                    ) {
                        timeline.forEach { point ->
                            val heightPercent = ((point.count.toDouble() / maxCount) * 100).coerceIn(6.0, 100.0)
                            val isHovered = hoveredPoint == point

                            Column(
                                modifier = Modifier
                                    .fillMaxHeight()
                                    .cursor(Cursor.Pointer)
                                    .onMouseEnter { hoveredPoint = point }
                                    .onMouseLeave { if (hoveredPoint == point) hoveredPoint = null },
                                verticalArrangement = Arrangement.Bottom,
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                // Bar
                                Box(
                                    modifier = ChartBarStyle.toModifier()
                                        .height(heightPercent.percent)
                                        .width(if (breakpoint >= Breakpoint.MD) 16.px else 10.px)
                                        .borderRadius(topLeft = 4.px, topRight = 4.px)
                                        .backgroundColor(if (isHovered) barColor else barColor.copyf(alpha = 0.55f))
                                )

                                // X-Axis Year label under the bar
                                val yearStr = point.period.take(4)
                                val showLabel = point.period.endsWith("-02") || point.period.endsWith("-01") || point.period == "2024-04" || point.period == "2025-04" || point.period == "2026-07"
                                SpanText(
                                    text = if (showLabel) yearStr else "·",
                                    modifier = Modifier
                                        .fontFamily(Constants.INTER)
                                        .fontSize(10.px)
                                        .color(subTextColor)
                                        .margin(top = 8.px)
                                )
                            }
                        }
                    }
                }
            }
        }
        */
    }
}
